import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { conversationsAPI, hasHistoryContext } from '../../services/api.js';
import { readPref, writePref } from '../../utils/storage.js';
import { basketReducer, basketStorageKey, mergeBaskets, sameBasket, sanitizeItems, toStoredItems } from './reportBasket.js';

// Panier du rapport composé (DOC-17), un par conversation, conservé côté serveur avec la
// conversation (BE-09) : il se retrouve d'un poste à l'autre.
//
// `scope` identifie la conversation affichée :
//   - « c:<id> »    conversation de l'historique : panier lu (GET) puis enregistré (PUT) sur
//                   /api/conversations/<id>/basket, cloisonné par demandeur comme l'historique ;
//   - « local:<n> » conversation sans historique (ou pas encore créée) : en mémoire seulement.
// Passage local → conversation (premier envoi) : le panier suit si la conversation n'en a pas.
// Toute autre bascule affiche le panier de la destination, dans le même rendu.
//
// Migration : l'ancien panier du stockage local (clé digitrans_report_basket:<id>) s'affiche
// aussitôt, puis il est fusionné après celui du serveur (8 éléments au plus) et retiré du poste
// dès que le serveur contient le résultat.
// Modifications faites avant la réponse du serveur : rejouées sur le panier reçu, en « ajouter »
// ou « retirer » selon ce qui était affiché (jamais une bascule à l'aveugle).
// Enregistrement : liste complète après un court regroupement, une requête à la fois ; une liste
// identique à celle du serveur n'est pas renvoyée ; nouvelles tentatives après un échec réseau
// ou serveur, puis au retour du réseau ou de l'onglet. Changement de conversation : l'envoi en
// attente part aussitôt ; fermeture de la page : envoi keepalive. Retour sur l'onglet : panier
// relu (modifié depuis un autre poste ?) s'il n'a pas changé ici entre-temps.

/** Délais en millisecondes : regroupement des modifications, nouvelles tentatives. */
export const BASKET_TIMING = { saveDelayMs: 400, retryDelaysMs: [1000, 3000, 10000, 30000] };

const conversationOf = (scope) => (typeof scope === 'string' && scope.startsWith('c:') ? scope.slice(2) : null);
// 400, 401, 404, 413 : renvoyer la même liste échouerait encore
const isRetryable = (err) => !err?.status || err.status >= 500 || err.status === 429 || err.status === 408;
const signatureOf = (items) => JSON.stringify(toStoredItems(items));
const readLegacy = (id) => sanitizeItems(readPref(basketStorageKey(id), []));
const dropLegacy = (id) => writePref(basketStorageKey(id), null);

// État à l'arrivée sur `scope`. Conversation de l'historique : panier provisoire (ancien
// panier local, sinon celui apporté au premier envoi) jusqu'à la réponse du serveur.
function enter(scope, previous, remote) {
  const id = conversationOf(scope);
  if (!id) return { scope, items: [], phase: 'local', queue: [], seed: null, loadFailed: false };
  const carried = previous?.scope?.startsWith('local:') ? previous.items : [];
  const legacy = readLegacy(id);
  const items = legacy.length ? legacy : carried;
  return remote
    ? { scope, items, phase: 'loading', queue: [], seed: { carried, legacy }, loadFailed: false }
    : { scope, items, phase: 'local', queue: [], seed: null, loadFailed: false };
}

/**
 * Panier de la conversation affichée, synchronisé avec le serveur.
 * @param {{scope: string, userContext: object|null, timing?: {saveDelayMs: number, retryDelaysMs: number[]}}} options
 *   userContext : contexte complet (entreprise + hash) quand l'historique est actif, sinon null.
 * @returns {{scope: string, items: object[], sync: 'local'|'loading'|'ready'|'error',
 *   dispatch: (action) => void, forget: (conversationId) => void}}
 *   sync : en mémoire seulement, lecture en cours, à jour (ou envoi en cours), échec (le panier
 *   reste utilisable sur ce poste, nouvel essai au retour du réseau ou de l'onglet).
 */
export function useReportBasketSync({ scope, userContext = null, timing = BASKET_TIMING }) {
  const remote = hasHistoryContext(userContext);
  const [state, setState] = useState(() => enter(scope, null, remote));
  // Conversation dont le dernier enregistrement a échoué définitivement
  const [saveError, setSaveError] = useState(null);
  const [loadKey, setLoadKey] = useState(0);

  // Autre conversation : son panier, dans le même rendu (jamais celui d'une autre conversation)
  if (state.scope !== scope) setState(enter(scope, state, remote));

  const contextRef = useRef(userContext);
  contextRef.current = userContext;
  const timingRef = useRef(timing);
  timingRef.current = timing;
  const stateRef = useRef(state);
  stateRef.current = state;

  const knownRef = useRef(new Map()); // conversation → signature du panier enregistré côté serveur
  const pendingRef = useRef(new Map()); // conversation → éléments à enregistrer (les plus récents)
  const failedRef = useRef(new Map()); // conversation → éléments non enregistrés (échec définitif)
  const legacyRef = useRef(new Set()); // ancien panier local à retirer après enregistrement
  const forgottenRef = useRef(new Set()); // conversations supprimées
  const inFlightRef = useRef(null); // conversation en cours d'envoi
  const debounceRef = useRef(null); // {id, timer}
  const retryRef = useRef({ timer: null, attempt: 0 });
  const versionRef = useRef(0); // modifications locales (relecture au retour sur l'onglet)
  const mountedRef = useRef(true);

  const clearError = useCallback((id) => {
    if (mountedRef.current) setSaveError((current) => (current === id ? null : current));
  }, []);

  // Envoie le prochain panier en attente ; enchaîne tant qu'il en reste
  const pump = useCallback(async () => {
    if (inFlightRef.current || retryRef.current.timer) return;
    const next = pendingRef.current.entries().next();
    if (next.done) return;
    const [id, items] = next.value;
    pendingRef.current.delete(id);
    const signature = signatureOf(items);
    if (forgottenRef.current.has(id) || knownRef.current.get(id) === signature) {
      pump();
      return;
    }
    inFlightRef.current = id;
    try {
      await conversationsAPI.saveBasket(id, toStoredItems(items), contextRef.current);
      knownRef.current.set(id, signature);
      retryRef.current.attempt = 0;
      if (legacyRef.current.delete(id)) dropLegacy(id);
      clearError(id);
    } catch (err) {
      if (err?.status === 404 || forgottenRef.current.has(id)) {
        // Conversation supprimée (ici ou depuis un autre poste) : plus rien à enregistrer
        retryRef.current.attempt = 0;
      } else if (isRetryable(err) && retryRef.current.attempt < timingRef.current.retryDelaysMs.length) {
        // Une liste plus récente a pu arriver entre-temps : elle prime
        if (!pendingRef.current.has(id)) pendingRef.current.set(id, items);
        const delay = timingRef.current.retryDelaysMs[retryRef.current.attempt];
        retryRef.current.attempt += 1;
        retryRef.current.timer = setTimeout(() => {
          retryRef.current.timer = null;
          pump();
        }, delay);
      } else {
        // Échec définitif : gardé pour le retour du réseau ou de l'onglet, signalé dans le panier
        // (sauf si une liste plus récente part ensuite)
        retryRef.current.attempt = 0;
        if (!pendingRef.current.has(id)) {
          failedRef.current.set(id, items);
          if (mountedRef.current) setSaveError(id);
        }
      }
    } finally {
      inFlightRef.current = null;
    }
    pump();
  }, [clearError]);

  /** Programme l'enregistrement de `items` pour la conversation `id`. */
  const schedule = useCallback((id, items) => {
    if (!id || forgottenRef.current.has(id)) return;
    failedRef.current.delete(id);
    if (knownRef.current.get(id) === signatureOf(items) && inFlightRef.current !== id) {
      // Le serveur a déjà ce panier : un envoi en attente pour cette conversation est caduc
      pendingRef.current.delete(id);
      if (debounceRef.current?.id === id) {
        clearTimeout(debounceRef.current.timer);
        debounceRef.current = null;
      }
      clearError(id);
      return;
    }
    pendingRef.current.set(id, items);
    // Délai en cours pour une AUTRE conversation : son envoi part tout de suite
    const otherWaiting = debounceRef.current && debounceRef.current.id !== id;
    if (debounceRef.current) clearTimeout(debounceRef.current.timer);
    debounceRef.current = {
      id,
      timer: setTimeout(() => {
        debounceRef.current = null;
        pump();
      }, timingRef.current.saveDelayMs),
    };
    if (otherWaiting) pump();
  }, [pump, clearError]);

  /** Envoi en attente : part tout de suite (changement de conversation). */
  const flush = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current.timer);
      debounceRef.current = null;
    }
    pump();
  }, [pump]);

  /** Conversation supprimée : plus rien à lire ni à enregistrer pour elle. */
  const forget = useCallback((id) => {
    if (!id) return;
    forgottenRef.current.add(id);
    pendingRef.current.delete(id);
    failedRef.current.delete(id);
    knownRef.current.delete(id);
    legacyRef.current.delete(id);
    if (debounceRef.current?.id === id) {
      clearTimeout(debounceRef.current.timer);
      debounceRef.current = null;
    }
    clearError(id);
  }, [clearError]);

  // Retour sur l'onglet : panier relu, s'il a changé depuis un autre poste. Abandonné si une
  // modification locale est en attente ou survient pendant la lecture (elle sera enregistrée).
  const refresh = useCallback(async (id) => {
    if (!id || inFlightRef.current === id || pendingRef.current.has(id) || failedRef.current.has(id)) return;
    const version = versionRef.current;
    let data;
    try {
      data = await conversationsAPI.getBasket(id, contextRef.current);
    } catch {
      return; // relu au prochain retour
    }
    const current = stateRef.current;
    if (!mountedRef.current || versionRef.current !== version || current.scope !== `c:${id}` || current.phase !== 'ready') return;
    if (inFlightRef.current === id || pendingRef.current.has(id)) return;
    const server = sanitizeItems(data?.items);
    knownRef.current.set(id, signatureOf(server));
    if (!sameBasket(server, current.items)) {
      setState((s) => (s.scope === current.scope && s.phase === 'ready' ? { ...s, items: server } : s));
    }
  }, []);

  // Lecture du panier de la conversation affichée (nouvel essai après un échec réseau ou serveur)
  useEffect(() => {
    if (state.phase !== 'loading') return undefined;
    const loadingScope = state.scope;
    const id = conversationOf(loadingScope);
    const { carried, legacy } = state.seed;
    const controller = new AbortController();
    let timer = null;
    let attempt = 0;
    const run = async () => {
      let data;
      try {
        data = await conversationsAPI.getBasket(id, contextRef.current, controller.signal);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.status === 404) {
          // Conversation supprimée entre-temps : le panier reste en mémoire
          setState((s) => (s.scope === loadingScope && s.phase === 'loading' ? { ...s, phase: 'local', queue: [], seed: null } : s));
          return;
        }
        const delays = timingRef.current.retryDelaysMs;
        if (isRetryable(err) && attempt < delays.length) {
          timer = setTimeout(run, delays[attempt]);
          attempt += 1;
        } else {
          setState((s) => (s.scope === loadingScope && s.phase === 'loading' ? { ...s, loadFailed: true } : s));
        }
        return;
      }
      if (controller.signal.aborted) return;
      const server = sanitizeItems(data?.items);
      knownRef.current.set(id, signatureOf(server));
      // Panier de la conversation : celui du serveur, puis l'ancien panier local ; à défaut,
      // celui apporté au premier envoi
      const destination = mergeBaskets(server, legacy);
      const base = carried.length && !destination.length ? carried : destination;
      if (legacy.length) {
        if (sameBasket(destination, server)) dropLegacy(id);
        else legacyRef.current.add(id);
      }
      setState((s) => (s.scope === loadingScope && s.phase === 'loading'
        ? { scope: s.scope, items: s.queue.reduce(basketReducer, base), phase: 'ready', queue: [], seed: null, loadFailed: false }
        : s));
    };
    run();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
    // state.seed est fixé en même temps que la phase « loading » de cette conversation
  }, [state.scope, state.phase, loadKey]);

  // Panier à jour : enregistrement différé (aucun envoi s'il est identique à celui du serveur)
  useEffect(() => {
    if (state.phase === 'ready') schedule(conversationOf(state.scope), state.items);
  }, [state.phase, state.scope, state.items, schedule]);

  // Changement de conversation : l'envoi en attente de la précédente part aussitôt
  useEffect(() => () => flush(), [state.scope, flush]);

  // Retour du réseau ou de l'onglet : enregistrements en échec renvoyés, lecture reprise ou
  // panier relu
  useEffect(() => {
    const wake = () => {
      // Échecs et nouvelle tentative en attente : renvoyés maintenant (un envoi encore en
      // regroupement attend la fin de son délai)
      const failed = [...failedRef.current];
      failedRef.current.clear();
      for (const [id, items] of failed) if (!pendingRef.current.has(id)) pendingRef.current.set(id, items);
      if (failed.length || retryRef.current.timer) {
        clearTimeout(retryRef.current.timer);
        retryRef.current = { timer: null, attempt: 0 };
        pump();
      }
      const current = stateRef.current;
      if (current.phase === 'loading' && current.loadFailed) setLoadKey((k) => k + 1);
      else if (current.phase === 'ready') refresh(conversationOf(current.scope));
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake();
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pump, refresh]);

  // Fermeture de la page : derniers paniers envoyés en keepalive (quelques Ko)
  useEffect(() => {
    const onPageHide = () => {
      for (const [id, items] of [...pendingRef.current, ...failedRef.current]) {
        if (forgottenRef.current.has(id) || knownRef.current.get(id) === signatureOf(items)) continue;
        conversationsAPI.saveBasket(id, toStoredItems(items), contextRef.current, { keepalive: true }).catch(() => {});
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current.timer);
      clearTimeout(retryRef.current.timer);
    };
  }, []);

  const dispatch = useCallback((action) => {
    versionRef.current += 1;
    setState((s) => {
      if (s.phase !== 'loading') {
        const items = basketReducer(s.items, action);
        return items === s.items ? s : { ...s, items };
      }
      // Panier du serveur pas encore reçu : action explicite, rejouée à sa réception
      const explicit = action.type === 'toggle' && action.item
        ? (s.items.some((i) => i.key === action.item.key) ? { type: 'remove', key: action.item.key } : { type: 'add', item: action.item })
        : action;
      const items = basketReducer(s.items, explicit);
      return items === s.items ? s : { ...s, items, queue: [...s.queue, explicit] };
    });
  }, []);

  const currentId = conversationOf(state.scope);
  const sync = state.phase === 'local'
    ? 'local'
    : state.loadFailed || (currentId !== null && saveError === currentId)
      ? 'error'
      : state.phase === 'loading' ? 'loading' : 'ready';

  return useMemo(
    () => ({ scope: state.scope, items: state.items, sync, dispatch, forget }),
    [state.scope, state.items, sync, dispatch, forget]
  );
}
